import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@shared/components/PageHeader";
import { Button } from "@shared/ui/button";
import { Plus, Box, UserCheck, UserX } from "lucide-react";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { Tabs } from "@shared/ui/tabs";
import AllResourcesTab from "./tabs/AllResources";
import InstalledTab from "./tabs/Installed";
import InWarehouseTab from "./tabs/InWarehouse";
import RetiredTab from "./tabs/Retired";
import RepairedTab from "./tabs/Repaired";
import DecisionModal from "../create/DecisionModal";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMeterSummary } from "../../hook";

export default function ResourcesLanding() {
  const navigate = useNavigate();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();
  const { data: meterCounts } = useMeterSummary(remoteUtilityId);

  const handleAddResource = () => {
    setIsAddModalOpen(true);
  };

  const tabComponents = {
    all: {
      label: "All resources",
      shortLabel: "All",
      count: meterCounts?.totalResources ?? meterCounts?.totalResources ?? 0,
      component: <AllResourcesTab />,
    },
    warehouse: {
      label: "In warehouse",
      shortLabel: "Warehouse",
      count: meterCounts?.inWarehouse ?? meterCounts?.inWarehouse ?? 0,
      component: <InWarehouseTab />,
    },
    installed: {
      label: "Installed",
      shortLabel: "Installed",
      count: meterCounts?.installed ?? 0,
      component: <InstalledTab />,
    },
    damaged: {
      label: "Repaired",
      shortLabel: "repaired",
      count: meterCounts?.repaired ?? 0,
      component: <RepairedTab />,
    },
    retired: {
      label: "Retired",
      shortLabel: "Retired",
      count: meterCounts?.retired ?? 0,
      component: <RetiredTab />,
    },
  };

  const urlMapping = {
    all: "all",
    installed: "installed",
    warehouse: "warehouse",
    damaged: "damaged",
    retired: "retired",
  };

  return (
    <div className="p-3 space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Resources"
        subtitle="Manage resource inventories, device types, and installation details."
      >
        <Button onClick={() => navigate('/onboarding/data-migration/data-cards')} className="flex items-center gap-1.5">
          <Plus className="w-4.5 h-4.5" />
          <span>Bulk Add resource</span>
        </Button>
        <Button onClick={handleAddResource} className="flex items-center gap-1.5">
          <Plus className="w-4.5 h-4.5" />
          <span>Add resource</span>
        </Button>
      </PageHeader>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickStatsCard
          title="Total resources"
          count={meterCounts?.totalResources ?? meterCounts?.total_resources ?? 0}
          trendSummary="Total number of resources in the system"
          icon={
            <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
              <Box className="w-6 h-6" />
            </div>
          }
          className="border-gray-200 shadow-sm"
        />
        <QuickStatsCard
          title="Assigned resources"
          count={meterCounts?.assigned ?? meterCounts?.ASSIGNED ?? 0}
          trendSummary="Resources currently assigned to consumers"
          icon={
            <div className="p-2.5 bg-green-50 text-green-600 rounded-lg">
              <UserCheck className="w-6 h-6" />
            </div>
          }
          className="border-gray-200 shadow-sm"
        />
        <QuickStatsCard
          title="Unassigned resources"
          count={meterCounts?.unassigned ?? meterCounts?.UNASSIGNED ?? 0}
          trendSummary="Resources available for assignment"
          icon={
            <div className="p-2.5 bg-slate-50 text-slate-600 rounded-lg">
              <UserX className="w-6 h-6" />
            </div>
          }
          className="border-gray-200 shadow-sm"
        />
      </div>

      {/* Tabs Layout */}
      <Tabs
        defaultValue="all"
        urlMapping={urlMapping}
        tabComponents={tabComponents}
        tabsListClassName="grid w-full grid-cols-5 mb-6"
        idPrefix="resources-list-tab"
        level={0}
      />

      {/* Add Resource Selection Modal */}
      <DecisionModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        onSelectWarehouse={() => {
          setIsAddModalOpen(false);
          navigate("/mx/manage/resources/create/warehouse");
        }}
        onSelectInstalled={() => {
          setIsAddModalOpen(false);
          navigate("/mx/manage/resources/create/installed");
        }}
      />
    </div>
  );
}
